import { GenericLabPreferenceCampaign, LaboratorioOption } from '../../interfaces/medicine';
import { MedicineService, MedicineLabInfo } from '../../components/search/medicineService';

const sponsoredByPrescription = new Map<number, Map<number, number>>();
const labInfoByMedicine = new Map<number, MedicineLabInfo>();

/**
 * Requisições em voo, memoizadas por chave.
 *
 * O cache de resultado só é preenchido quando a resposta chega, então chamadas paralelas
 * (aplicar um modelo dispara um `resolveGenericLabInfo` por medicamento ao mesmo tempo)
 * errariam o cache e repetiriam a mesma requisição N vezes. Guardando a promessa, a
 * primeira chamada faz a requisição e as demais aguardam o mesmo resultado.
 */
const inFlightSponsored = new Map<number, Promise<Map<number, number>>>();
const inFlightLabInfo = new Map<number, Promise<MedicineLabInfo>>();

function buildSponsoredMap(campaigns: GenericLabPreferenceCampaign[]): Map<number, number> {
  const sponsored = new Map<number, number>();

  campaigns.forEach((campaign) => {
    campaign.products?.forEach((product) => {
      const preference = product.genericLabPreference;
      if (preference?.active) {
        sponsored.set(preference.medicineId, preference.laboratoryId);
      }
    });
  });

  return sponsored;
}

/**
 * Mapa `medicamento -> laboratório patrocinado` das campanhas ativas da prescrição.
 *
 * @param prescriptionId Prescrição em contexto.
 * @param doctorId Prescritor dono da prescrição.
 * @param partnerId Parceiro do prescritor, quando disponível.
 * @returns Mapa das preferências patrocinadas; vazio quando não há campanha.
 */
async function getSponsoredMap(
  prescriptionId: number,
  doctorId: number,
  partnerId?: number
): Promise<Map<number, number>> {
  const cached = sponsoredByPrescription.get(prescriptionId);
  if (cached) return cached;

  const pending = inFlightSponsored.get(prescriptionId);
  if (pending) return pending;

  const request = MedicineService.getGenericLabPreferences(prescriptionId, doctorId, partnerId)
    .then((campaigns) => {
      if (campaigns == null) return new Map<number, number>();

      const sponsored = buildSponsoredMap(campaigns);
      sponsoredByPrescription.set(prescriptionId, sponsored);
      return sponsored;
    })
    .finally(() => {
      inFlightSponsored.delete(prescriptionId);
    });

  inFlightSponsored.set(prescriptionId, request);
  return request;
}

const EMPTY_LAB_INFO: MedicineLabInfo = { laboratorios: [], tipoMedicamento: '' };

/**
 * Dados de catálogo do medicamento usados na escolha de laboratório de genéricos.
 *
 * @param medicineId Medicamento do catálogo Mevo.
 * @param prescriptionId Prescrição em contexto, exigida pelo endpoint.
 * @returns Laboratórios disponíveis e o tipo do medicamento.
 */
async function getLabInfo(medicineId: number, prescriptionId: number): Promise<MedicineLabInfo> {
  const cached = labInfoByMedicine.get(medicineId);
  if (cached) return cached;

  const pending = inFlightLabInfo.get(medicineId);
  if (pending) return pending;

  const request = MedicineService.getMedicineLabInfo(medicineId, prescriptionId)
    .then((labInfo) => {
      if (!labInfo?.laboratorios.length) return labInfo ?? EMPTY_LAB_INFO;

      labInfoByMedicine.set(medicineId, labInfo);
      return labInfo;
    })
    .finally(() => {
      inFlightLabInfo.delete(medicineId);
    });

  inFlightLabInfo.set(medicineId, request);
  return request;
}

export interface GenericLabInfo {
  laboratorios: LaboratorioOption[];
  idLaboratorioPatrocinado: number | null;
  /**
   * Classificação do catálogo: `true` para genérico, `false` para outro tipo e `null` quando
   * o detalhe do medicamento não informa o tipo. Ausência de campo não é o mesmo que negativa,
   * então o chamador decide o que fazer com o `null`.
   */
  isGeneric: boolean | null;
}

/**
 * Reúne os dados necessários para escolher o laboratório de um genérico.
 *
 * @param params Medicamento e contexto (prescrição, prescritor e parceiro).
 * @returns Laboratórios disponíveis, o patrocinado da campanha e se o medicamento é genérico.
 */
export async function resolveGenericLabInfo(params: {
  medicineId: number;
  prescriptionId: number;
  doctorId: number;
  partnerId?: number;
}): Promise<GenericLabInfo> {
  const { medicineId, prescriptionId, doctorId, partnerId } = params;

  const [labInfo, sponsored] = await Promise.all([
    getLabInfo(medicineId, prescriptionId),
    getSponsoredMap(prescriptionId, doctorId, partnerId),
  ]);

  return {
    laboratorios: labInfo.laboratorios,
    idLaboratorioPatrocinado: sponsored.get(medicineId) ?? null,
    isGeneric: labInfo.tipoMedicamento ? labInfo.tipoMedicamento === 'GENERICO' : null,
  };
}
